<template>
  <Card
    :footerDivider="false"
    :title="$t('publicPasswordResetInit.initialization.title')"
  >
    <template v-slot:content>
      <p>
        {{ $t('publicPasswordResetInit.initialization.description') }}
      </p>
      <div>
        <div id="username">
          <BasicTextfield
            @input="onUsernameInputUpdated"
            id="password-reset.username"
            :label="$t('publicPasswordResetInit.username.label')"
            :required="true"
            :status="usernameTextfieldStatus"
            :value="username"
            @blur="setUsernameToDefaultStatus"
            @cleared="setUsernameToDefaultStatus"
            @error="toggleUsernameError"
            @update:input="validateUsername"
            @keyup.enter="onSubmit"
          >
            <template v-slot:error>{{ usernameErrorMessage }}</template>
            <template v-slot:info>
              <div v-dompurify-html="$t('publicPasswordResetInit.username.info')"></div>
            </template>
          </BasicTextfield>
        </div>
        <div class="card-row__col1"></div>
      </div>
    </template>
    <template v-slot:footer>
      <div class="footer-cta footer-cta--left">
        <CustomButton
          @click="onSubmit"
          :button-color="ButtonColor.PRIMARY"
          :pending-state="pending"
          buttonId="password-reset.username-bestaetigen-button"
          >{{ $t('publicPasswordResetInit.submit.text') }}
        </CustomButton>
        <CustomButton
          @click="goToUsernameForgotPage"
          :buttonColor="ButtonColor.LINK"
          buttonId="password-reset.forgot-username-link"
        >
          {{ $t('publicPasswordResetInit.forgotUsername.linkText') }}
        </CustomButton>
      </div>
    </template>
  </Card>
</template>

<script lang="ts" setup>
import {
  BasicTextfield,
  ButtonColor,
  Card,
  CustomButton,
  InputStatus
} from 'de-zurich-ui-library'
import { navigate } from '@/utils/NavigationUtils'
import { onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { is5xxError } from '@/components/account-management/accountcreation/accountcreationUtils'
import { initiatePasswordReset } from '@/stores/accountManagementService'
import { ResponseError } from 'account-api'

const { t } = useI18n()

const props = defineProps({
  onEmailSent: {
    type: Function,
    required: true
  }
})

const username = ref('')
const usernameTextfieldStatus = ref<InputStatus>(InputStatus.DEFAULT)
const usernameErrorMessage = ref('')
const pending = ref(false)

onMounted(() => {
  username.value = new URLSearchParams(globalThis.location.search).get('username') || ''
})

const validateUsername = (): void => {
  if (!username.value || username.value === '') {
    usernameTextfieldStatus.value = InputStatus.ERROR
    usernameErrorMessage.value = t('publicPasswordResetInit.username.errors.empty')
  } else if (username.value.length < 7) {
    usernameErrorMessage.value = t('publicPasswordResetInit.username.errors.tooShort', {
      chars: username.value.length
    })
    usernameTextfieldStatus.value = InputStatus.ERROR
  } else if (username.value.length > 60) {
    usernameErrorMessage.value = t('publicPasswordResetInit.username.errors.tooLong', {
      chars: username.value.length
    })
    usernameTextfieldStatus.value = InputStatus.ERROR
  } else if (!/^[A-Za-z0-9_\-.;,#+><|[\]!$%&/@]+$/.test(username.value)) {
    usernameErrorMessage.value = t(
      'publicPasswordResetInit.username.errors.rulesViolated'
    )
    usernameTextfieldStatus.value = InputStatus.ERROR
  }
}

const onUsernameInputUpdated = (val: string): void => {
  username.value = val
  usernameTextfieldStatus.value = InputStatus.VALID
}

const toggleUsernameError = (): void => {
  usernameTextfieldStatus.value = InputStatus.ERROR
}

const setUsernameToDefaultStatus = (): void => {
  usernameTextfieldStatus.value = InputStatus.DEFAULT
}

const onSubmit = async (): Promise<void> => {
  validateUsername()

  if (usernameTextfieldStatus.value === InputStatus.ERROR) {
    return
  }

  pending.value = true

  try {
    await initiatePasswordReset(username.value, false)
    // Success: no error thrown, call onEmailSent callback
    // Note: response headers (x-reset-code, x-reset-validity) are no longer available from the service
    // They were only present when backend was not connected to textplattform
    props.onEmailSent(username.value, {})
  } catch (e) {
    if (e instanceof ResponseError) {
      const status = e.response.status
      if (is5xxError(status)) {
        navigate(globalThis.ENV_CONFIG?.REDIRECTS.TECHNICAL_ERROR_PAGE)
      } else {
        // 4xx errors treated as invalid username
        usernameErrorMessage.value = t('publicPasswordResetInit.username.errors.invalid')
        usernameTextfieldStatus.value = InputStatus.ERROR
      }
    } else {
      // Network or other errors
      navigate(globalThis.ENV_CONFIG?.REDIRECTS.TECHNICAL_ERROR_PAGE)
    }
  } finally {
    pending.value = false
  }
}

const goToUsernameForgotPage = (): void => {
  navigate(globalThis.ENV_CONFIG?.REDIRECTS.ACCOUNT_USERNAME_RESET_PAGE)
}
</script>

<style lang="scss" scoped>
span.error-message {
  font-size: 24px;
  color: var(--input-color-error);
}

b.error-message {
  padding-left: 5px;
  color: var(--input-color-error);
}

.card {
  min-height: 250px;
}
</style>
